import React, { useState, useEffect } from 'react';
import { TierCounts, PromoCodeValidationResponse } from '../../types';
import PromoCodeInput from './PromoCodeInput';

interface CostPreviewModalProps {
  isOpen: boolean;
  leadCount: number;
  tierBreakdown: TierCounts;
  filters: Record<string, string | string[]>;
  onConfirm: (finalCost: number, promoCode?: PromoCodeValidationResponse) => void;
  onCancel: () => void;
}

// Pricing constants matching the backend
const BASE_PRICE = 50; // $50 per 1000 leads
const TIER_PRICING = {
  1: 0.10,
  2: 0.08,
  3: 0.05,
  4: 0.03,
  5: 0.01,
};

const CostPreviewModal: React.FC<CostPreviewModalProps> = ({
  isOpen,
  leadCount,
  tierBreakdown,
  filters,
  onConfirm,
  onCancel
}) => {
  const [appliedPromoCode, setAppliedPromoCode] = useState<PromoCodeValidationResponse | null>(null);
  const [originalCost, setOriginalCost] = useState(0);
  const [finalCost, setFinalCost] = useState(0);

  // Calculate costs
  useEffect(() => {
    const baseCost = BASE_PRICE * (leadCount / 1000);
    let tierUpcharge = 0;
    
    for (let tier = 1; tier <= 5; tier++) {
      const count = tierBreakdown[tier] || 0;
      tierUpcharge += count * (TIER_PRICING[tier as keyof typeof TIER_PRICING] || 0);
    }
    
    const total = baseCost + tierUpcharge;
    setOriginalCost(total);
    
    // Apply promo code discount if available
    if (appliedPromoCode && appliedPromoCode.isValid) {
      setFinalCost(appliedPromoCode.finalAmount);
    } else {
      setFinalCost(total);
    }
  }, [leadCount, tierBreakdown, appliedPromoCode]);

  const handlePromoCodeApplied = (validation: PromoCodeValidationResponse) => {
    setAppliedPromoCode(validation);
  };

  const handlePromoCodeRemoved = () => {
    setAppliedPromoCode(null);
  };

  const handleConfirm = () => {
    onConfirm(finalCost, appliedPromoCode || undefined);
  };

  if (!isOpen) return null;

  const discountAmount = originalCost - finalCost;
  const hasDiscount = discountAmount > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Export Cost Preview</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Lead Count Info */}
        <div className="bg-purple-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-800">Leads to Export</span>
            <span className="text-lg font-bold text-purple-900">{leadCount}</span>
          </div>
          <div className="text-xs text-purple-600">
            Filtered by: {Object.entries(filters).map(([key, value]) => 
              value ? `${key}: ${value}` : null
            ).filter(Boolean).join(', ') || 'No filters'}
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="border border-gray-200 rounded-lg p-4 mb-4">
          <h3 className="font-medium text-gray-900 mb-3">Cost Breakdown</h3>
          
          {/* Base Cost */}
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Base cost ({leadCount} leads)</span>
            <span className="text-sm">${(BASE_PRICE * (leadCount / 1000)).toFixed(2)}</span>
          </div>

          {/* Tier Upcharges */}
          {Object.entries(tierBreakdown).map(([tier, count]) => {
            if (count === 0) return null;
            const tierPrice = TIER_PRICING[parseInt(tier) as keyof typeof TIER_PRICING] || 0;
            const tierCost = count * tierPrice;
            
            return (
              <div key={tier} className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  Tier {tier} leads ({count} × ${tierPrice.toFixed(2)})
                </span>
                <span className="text-sm">${tierCost.toFixed(2)}</span>
              </div>
            );
          })}

          {/* Subtotal */}
          <div className="border-t border-gray-200 pt-2 mt-2">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-900">Subtotal</span>
              <span className="font-medium">${originalCost.toFixed(2)}</span>
            </div>
          </div>

          {/* Discount */}
          {hasDiscount && (
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-green-600">
                Discount ({appliedPromoCode?.discountPercentage}% off)
              </span>
              <span className="text-sm text-green-600">-${discountAmount.toFixed(2)}</span>
            </div>
          )}

          {/* Final Total */}
          <div className="border-t border-gray-200 pt-2 mt-2">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-gray-900">Total</span>
              <span className={`text-lg font-bold ${hasDiscount ? 'text-green-600' : 'text-gray-900'}`}>
                ${finalCost.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Promo Code Input */}
        <div className="mb-6">
          <PromoCodeInput
            selectedPlan="lead_export"
            originalAmount={originalCost}
            onCodeApplied={handlePromoCodeApplied}
            onCodeRemoved={handlePromoCodeRemoved}
          />
        </div>

        {/* Pricing Info */}
        <div className="bg-gray-50 rounded-lg p-3 mb-6">
          <div className="text-xs text-gray-600">
            <div className="mb-1">• Base: $50 per 1,000 leads</div>
            <div className="mb-1">• Tier 1-5 surcharge: $0.10, $0.08, $0.05, $0.03, $0.01 per lead</div>
            <div>• Credits will be added to your account after payment</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
          >
            Purchase Credits (${finalCost.toFixed(2)})
          </button>
        </div>
      </div>
    </div>
  );
};

export default CostPreviewModal;